import * as React from 'react';

import Image from 'next/image';
import { useTheme } from 'styled-components';
import { AvatarResponsiveSizeType, AvatarSizeType, AvatarVariantType } from '../../../theme/type/avatar-type';
import { BreakpointSizeType } from '../../../theme/type/breakpoints-type';
import { breakpointPrefix, cn, textSizeClass } from '../../../utils/tailwind';

const avatarSizeClass: Record<AvatarSizeType, string> = {
    xsmall: 'h-[3rem] w-[3rem] text-xsmall',
    small: 'h-[3.2rem] w-[3.2rem] text-small',
    medium: 'h-[4rem] w-[4rem] text-medium',
    large: 'h-[4.8rem] w-[4.8rem] text-large',
};

const getResponsiveSizeClasses = (size: AvatarResponsiveSizeType) => {
    const entries = Object.entries(size) as [BreakpointSizeType, AvatarSizeType][];

    return entries.map(([breakpoint, value], index) => {
        const className = cn(avatarSizeClass[value], textSizeClass[value]);

        return index === 0
            ? className
            : className
                  .split(' ')
                  .filter(Boolean)
                  .map((token) => `${breakpointPrefix[breakpoint]}${token}`)
                  .join(' ');
    });
};

interface AvatarPropsInterface {
    initials?: string;
    variant?: AvatarVariantType;
    image?: string;
    shadow?: boolean;
    size?: AvatarSizeType | AvatarResponsiveSizeType;
    onClick?: (event: React.MouseEvent<HTMLAnchorElement & HTMLButtonElement & HTMLDivElement>) => void;
    onMouseEnter?: (event: React.MouseEvent<HTMLDivElement>) => void;
    onMouseLeave?: (event: React.MouseEvent<HTMLDivElement>) => void;
}

export const Avatar: React.FC<AvatarPropsInterface> = ({
    variant = 'default',
    initials,
    image,
    size = 'medium',
    shadow = false,
    onClick,
    onMouseEnter,
    onMouseLeave,
}) => {
    const theme = useTheme();

    let dimension = 4.0;
    if (size === 'small') {
        dimension = 3.2;
    } else if (size === 'large') {
        dimension = 4.8;
    }

    let sizeClassName = avatarSizeClass.medium;

    if (typeof size === 'string') {
        sizeClassName = avatarSizeClass[size];
    } else if (size) {
        sizeClassName = getResponsiveSizeClasses(size).join(' ');
    }

    const currentVariant = theme.avatar.variant[variant];

    return (
        <span
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            className={cn(
                'inline-flex items-center justify-center rounded-full font-black [&_img]:rounded-full',
                sizeClassName,
                shadow && 'shadow-soft',
                onClick && 'cursor-pointer',
            )}
            style={
                {
                    color: currentVariant.color,
                    backgroundColor: currentVariant.background,
                } as React.CSSProperties
            }
        >
            {image && initials && (
                <Image src={image} layout="intrinsic" width={dimension * 10} height={dimension * 10} alt={initials} />
            )}
            {!image && initials}
        </span>
    );
};
